import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  EMPTY,
  fromEvent,
  merge,
  Observable,
  timer,
} from 'rxjs';
import {
  catchError,
  filter,
  switchMap,
  take,
  throttleTime,
} from 'rxjs/operators';

interface VersionInfo {
  sha: string;
  builtAt?: string;
}

@Injectable()
export class VersionCheckService {
  private static readonly POLL_INTERVAL_MS = 5 * 60 * 1000;
  private static readonly FOCUS_THROTTLE_MS = 60 * 1000;

  // Built on HttpBackend so the poll skips the interceptor chain
  // (no auth header, no RetryInterceptor storm on a flaky response).
  private readonly http: HttpClient;
  private readonly updateAvailableSource = new BehaviorSubject<boolean>(false);
  public readonly updateAvailable$: Observable<boolean> =
    this.updateAvailableSource.asObservable();

  constructor(backend: HttpBackend) {
    this.http = new HttpClient(backend);
  }

  /**
   * Starts polling /version.json (emitted by CI next to the bundle) and flips
   * updateAvailable$ once the deployed sha differs from the running build.
   * No-op for local builds, where CI_COMMIT_SHA is not injected.
   */
  public start(currentSha: string | undefined): void {
    if (!currentSha) {
      return;
    }

    merge(
      timer(
        VersionCheckService.POLL_INTERVAL_MS,
        VersionCheckService.POLL_INTERVAL_MS
      ),
      fromEvent(window, 'focus').pipe(
        throttleTime(VersionCheckService.FOCUS_THROTTLE_MS)
      )
    )
      .pipe(
        switchMap(() =>
          this.http
            .get<VersionInfo>(`/version.json?t=${Date.now()}`)
            .pipe(catchError(() => EMPTY))
        ),
        filter((info) => !!info && !!info.sha && info.sha !== currentSha),
        take(1)
      )
      .subscribe(() => this.updateAvailableSource.next(true));
  }
}
